import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt'
import { faTrophy } from '@fortawesome/pro-solid-svg-icons/faTrophy'
import { faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons/faArrowRotateLeft'
import { faBullseyeArrow } from '@fortawesome/pro-solid-svg-icons/faBullseyeArrow'
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye'
import { faFaceFrown } from '@fortawesome/pro-solid-svg-icons/faFaceFrown'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import { Button, Spin } from 'antd'
import { useState } from 'react'
import EmptyState from 'components/reusables/emptyState'
import { IEvaluationResume } from 'models/evaluation.model'
import { evaluationsService } from 'api/courses/evaluations'
import { Information, Content, Buttons } from './styled'

interface IProps {
  evaluation: IEvaluationResume
  showEvaluation: () => void
}

const Started = ({ evaluation, showEvaluation }: IProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const translationPrefix = 'lessons.contentBlocks.evaluationBlock.detail'

  const handleRetry = async () => {
    setLoading(true)
    try {
      await evaluationsService.resetEvaluationResponse(
        evaluation.evaluationResponse?.id || 0,
        false,
        true
      )
      showEvaluation()
    } finally {
      setLoading(false)
    }
  }

  const hasRetries =
    !evaluation.multipleRetries || evaluation.attemptsRemaining > 0
  return (
    <>
      <Information>
        <EmptyState
          title={t(
            `${translationPrefix}.${
              evaluation.approved
                ? 'evaluationApproved'
                : hasRetries
                ? 'failedWithRetries'
                : 'failedWithoutRetries'
            }.title`
          )}
          description={t(
            `${translationPrefix}.${
              evaluation.approved
                ? 'evaluationApproved'
                : hasRetries
                ? 'failedWithRetries'
                : 'failedWithoutRetries'
            }.description`
          )}
          icon={evaluation.approved ? faTrophy : faFaceFrown}
          iconColor="#213F68"
          iconSize="large"
          iconBackground={evaluation.approved ? '#AFECD3' : '#FAC1C1'}
          iconBadge={evaluation.approved ? faCheck : faTimes}
          iconBadgeBackground={evaluation.approved ? '#00B47C' : '#F5222D'}
          padding="16px 0 0 0"
          titleFontSize="16px"
          descriptionFontSize="16px"
          titleColor="#213F68"
          descriptionColor="#213F68"
        />
      </Information>
      <Content approved={evaluation.approved}>
        <div>
          <FontAwesomeIcon icon={faBolt} />
          <span>{evaluation.obtainedPoints}</span>
          <div>{t(`${translationPrefix}.pointsObtained`)}</div>
        </div>
        <div>
          <FontAwesomeIcon icon={faTrophy} />
          <span>{evaluation.minimumPassingScore}</span>
          <div>{t(`${translationPrefix}.requiredScore`)}</div>
        </div>
        <div>
          <FontAwesomeIcon icon={faBullseyeArrow} />
          <span>{`${evaluation.correctAnswers} ${t(
            `${translationPrefix}.of`
          )} ${evaluation.totalQuestions}`}</span>
          <div>{t(`${translationPrefix}.correctAnswers`)}</div>
        </div>
        {evaluation.multipleRetries && (
          <div>
            <FontAwesomeIcon icon={faArrowRotateLeft} />
            <span>{`${evaluation.attemptsRemaining} ${t(
              `${translationPrefix}.of`
            )} ${evaluation.maxRetries}`}</span>
            <div>{t(`${translationPrefix}.attemptsAllowed`)}</div>
          </div>
        )}
        {!evaluation.multipleRetries && (
          <div>
            <FontAwesomeIcon icon={faArrowRotateLeft} />
            <span>{t(`${translationPrefix}.unlimitedAttempts`)}</span>
            <div>{t(`${translationPrefix}.attemptsAllowed`)}</div>
          </div>
        )}
      </Content>
      <Buttons>
        <Button onClick={showEvaluation} disabled={!evaluation.showResults}>
          {t(`${translationPrefix}.buttons.reviewAnswers`)}
          <FontAwesomeIcon icon={faEye} />
        </Button>
        <Spin spinning={loading}>
          <Button
            onClick={handleRetry}
            disabled={!hasRetries || evaluation.approved}
          >
            {t(`${translationPrefix}.buttons.retry`)}
            <FontAwesomeIcon icon={faArrowRotateLeft} />
          </Button>
        </Spin>
      </Buttons>
    </>
  )
}

export default Started
